/**
 * 工具管理 API 服务
 * 系统级工具管理（添加、编辑、删除、列表、导入）
 * 调用 axiom-agent-admin 服务
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type { PaginatedResponse, SuccessResponse } from '@/shared/types/api'
import type {
  Tool,
  ToolListItem,
  ToolUsage,
  CreateToolRequest,
  UpdateToolRequest,
  ToolListQuery,
  BuiltinToolsResponse,
  BuiltinToolInfo,
} from '@/modules/agent/types/tool'

/**
 * 工具管理 API 服务
 */
export class ToolApiService {
  /**
   * 获取工具列表（分页）
   * 注意：使用游标分页（limit、last_key），不支持 page/page_size
   * 返回类型：PaginatedResponse（包含 success: true, data, pagination, meta）
   */
  static async getTools(
    query?: ToolListQuery
  ): Promise<PaginatedResponse<ToolListItem>> {
    const params = new URLSearchParams()
    if (query?.category) params.append('category', query.category)
    if (query?.search) params.append('search', query.search)
    if (query?.limit) params.append('limit', query.limit.toString())
    if (query?.last_key) params.append('last_key', query.last_key)

    const queryString = params.toString()
    // 使用专门的分页响应处理方法
    return ApiClient.getPaginatedWithClient<ToolListItem>(
      apiClients.agentAdmin,
      `/tools${queryString ? `?${queryString}` : ''}`
    )
  }

  /**
   * 获取工具详情
   */
  static async getTool(toolId: string): Promise<SuccessResponse<Tool>> {
    return ApiClient.getWithClient<Tool>(
      apiClients.agentAdmin,
      `/tools/${toolId}`
    )
  }

  /**
   * 创建工具
   */
  static async createTool(
    data: CreateToolRequest
  ): Promise<SuccessResponse<Tool>> {
    return ApiClient.postWithClient<Tool>(
      apiClients.agentAdmin,
      '/tools',
      data
    )
  }

  /**
   * 更新工具
   */
  static async updateTool(
    toolId: string,
    data: UpdateToolRequest
  ): Promise<SuccessResponse<Tool>> {
    return ApiClient.putWithClient<Tool>(
      apiClients.agentAdmin,
      `/tools/${toolId}`,
      data
    )
  }

  /**
   * 获取工具使用情况
   */
  static async getToolUsage(
    toolId: string
  ): Promise<SuccessResponse<{ items: ToolUsage[] }>> {
    return ApiClient.getWithClient<{ items: ToolUsage[] }>(
      apiClients.agentAdmin,
      `/tools/${toolId}/usage`
    )
  }

  /**
   * 删除工具
   */
  static async deleteTool(
    toolId: string
  ): Promise<SuccessResponse<{ message: string }>> {
    return ApiClient.deleteWithClient<{ message: string }>(
      apiClients.agentAdmin,
      `/tools/${toolId}`
    )
  }

  /**
   * 批量导入工具（JSON 配置）
   * 后端期望格式：{ config: { version: "1.0", tools: [...] }, category: "分类名", scope: "internal|external" }
   */
  static async importTools(
    config: Record<string, unknown>,
    category?: string,
    scope?: "internal" | "external"
  ): Promise<SuccessResponse<{ success_count: number; imported: ToolListItem[] }>> {
    return ApiClient.postWithClient<{ success_count: number; imported: ToolListItem[] }>(
      apiClients.agentAdmin,
      '/tools/import',
      { config, category, scope }
    )
  }

  /**
   * 获取上下文参数配置
   * GET /api/v1/settings/context-params
   */
  static async getContextParams(): Promise<SuccessResponse<{ params: string[] }>> {
    return ApiClient.getWithClient<{ params: string[] }>(
      apiClients.agentAdmin,
      '/settings/context-params'
    )
  }

  /**
   * 更新上下文参数配置
   * PUT /api/v1/settings/context-params
   */
  static async updateContextParams(params: string[]): Promise<SuccessResponse<{ params: string[] }>> {
    return ApiClient.putWithClient<{ params: string[] }>(
      apiClients.agentAdmin,
      '/settings/context-params',
      { params }
    )
  }

  /**
   * 添加单个上下文参数
   * POST /api/v1/settings/context-params/add
   */
  static async addContextParam(param: string): Promise<SuccessResponse<{ params: string[] }>> {
    return ApiClient.postWithClient<{ params: string[] }>(
      apiClients.agentAdmin,
      '/settings/context-params/add',
      { param }
    )
  }

  /**
   * 移除单个上下文参数
   * POST /api/v1/settings/context-params/remove
   */
  static async removeContextParam(param: string): Promise<SuccessResponse<{ params: string[] }>> {
    return ApiClient.postWithClient<{ params: string[] }>(
      apiClients.agentAdmin,
      '/settings/context-params/remove',
      { param }
    )
  }

  /**
   * 获取项目可用工具列表
   * GET /api/v1/projects/{project_id}/tools
   * v4.0 新增：返回项目 enabled_tool_ids 中配置的工具
   */
  static async getProjectTools(
    projectId: string
  ): Promise<PaginatedResponse<ToolListItem>> {
    return ApiClient.getPaginatedWithClient<ToolListItem>(
      apiClients.agentAdmin,
      `/projects/${projectId}/tools`
    )
  }

  /**
   * 获取所有 builtin tools 列表
   * GET /api/v1/builtin-tools
   * 新增：获取系统内置工具（heartbeat、workspace、skills、code）
   */
  static async getBuiltinTools(): Promise<SuccessResponse<BuiltinToolsResponse>> {
    return ApiClient.getWithClient<BuiltinToolsResponse>(
      apiClients.agentAdmin,
      '/builtin-tools'
    )
  }

  /**
   * 获取特定 builtin tool 详情
   * GET /api/v1/builtin-tools/{tool_name}
   * 新增：获取单个内置工具的详细信息
   */
  static async getBuiltinTool(toolName: string): Promise<SuccessResponse<BuiltinToolInfo>> {
    return ApiClient.getWithClient<BuiltinToolInfo>(
      apiClients.agentAdmin,
      `/builtin-tools/${toolName}`
    )
  }
}

